import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { themes, ThemeName, getTheme } from '../theme'

interface ThemeContextType {
  currentTheme: ThemeName
  setTheme: (themeName: ThemeName) => void
  availableThemes: ThemeName[]
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_STORAGE_KEY = 'veilstream-theme'

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>('Evergreen Dark')

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeName
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme)
    }
  }, [])

  // Save theme to localStorage when it changes
  const setTheme = (themeName: ThemeName) => {
    setCurrentTheme(themeName)
    localStorage.setItem(THEME_STORAGE_KEY, themeName)
  }

  const availableThemes = Object.keys(themes) as ThemeName[]

  const value: ThemeContextType = {
    currentTheme,
    setTheme,
    availableThemes
  }

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={getTheme(currentTheme)}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}
